import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { AuthService } from '@klickdata/core/auth';
import { File, FileData, FileService } from '@klickdata/core/file';
import { LanguageService } from '@klickdata/core/localization';
import { Media } from '@klickdata/core/media/src/media.model';
import { SideNaveActionsTypes, SideNaveData, SideNaveResponseData } from '@klickdata/core/mobile/src/mobile.service';
import { AppScope } from '@klickdata/core/resource';
import { User } from '@klickdata/core/user';
import { Utils } from '@klickdata/core/util';
import { Observable, Subject, of } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-menu-side-add-media-file',
    templateUrl: './menu-side-add-media-file.component.html',
    styleUrls: ['./menu-side-add-media-file.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuSideAddMediaFileComponent implements OnDestroy, OnInit {
    @Input() public navData: SideNaveData;
    @Output() onClose: EventEmitter<SideNaveResponseData> = new EventEmitter();
    public fileForm: FormGroup;
    public file: File;
    public authUser$: Observable<User>;
    AppScope = AppScope;
    @Output() saving: EventEmitter<boolean> = new EventEmitter<boolean>();
    public currentLaguageId$: Observable<number>;
    private destroy: Subject<boolean> = new Subject<boolean>();

    constructor(
        protected fb: FormBuilder,
        protected fileService: FileService,
        protected langService: LanguageService,
        protected authService: AuthService
    ) {
        this.fileForm = this.fb.group({
            title: [''],
            description: [''],
            priority: ['a3'],
            visibility: [''],
            tag_ids: [[]],
            user_ids: [[]],
            group_ids: [[]],
            staff: this.fb.group({
                manager: [[]],
            }),
            medias: this.fb.group({
                file: [[]],
            }),
        });
    }
    ngOnInit(): void {
        this.authUser$ = this.authService.getUser();
        this.currentLaguageId$ = this.langService
            .getLanguageByKey(this.langService.getCurrentLanguage().value)
            .pipe(map((lang) => lang.id));
        if (this.navData.data.file) {
            this.file = this.navData.data.file;
            this.fileForm.patchValue(this.navData.data.file);
        }
    }
    onUploadComplete(mediaItems: Media[]) {
        if (this.fileForm.value.title) {
            return;
        }
        if (mediaItems && !!mediaItems.length) {
            this.fileForm.get('title').patchValue(mediaItems[0]?.filename);
            this.fileForm.get('title').markAsDirty();
        }
    }
    public submit() {
        if (!this.fileForm.value.medias?.file?.length) {
            return;
        }
        this.saving.emit(true);
        this.prepareData()
            .pipe(
                switchMap((data) =>
                    this.file?.id
                        ? this.fileService.update(data, ['author', 'tags', 'user_feeds', 'managers'])
                        : this.fileService.create(data, ['author', 'tags', 'user_feeds', 'managers'])
                ),
                takeUntil(this.destroy)
            )
            .subscribe((file) => {
                this.saving.emit(false);
                this.onClose.emit({
                    action: SideNaveActionsTypes.POSITIVE,
                    data: { value: file, type: this.navData.data.type, visibility: this.fileForm.value.visibility },
                });
                this.fileForm.reset();
            });
    }
    prepareData(): Observable<FileData> {
        const formData: FileData = Utils.changedControls(this.fileForm);
        const data = this.file?.id ? this.file.getPayload(formData) : new File(formData).getPayload();

        if (this.file?.id) {
            data.id = this.file.id;
        }

        // Always take latest audience selections from the form
        // even if the underlying controls are still pristine.
        data.user_ids = this.fileForm.value.user_ids;
        data.group_ids = this.fileForm.value.group_ids;
        data.visibility = this.fileForm.value.visibility;
        data.staff = {
            manager: this.fileForm.value.staff?.manager,
        };
        if (this.navData.data.customer_id) {
            data.customer_ids = [this.navData.data.customer_id];
        }

        return of(data);
    }
    ngOnDestroy() {
        this.destroy.next(true);
        this.destroy.unsubscribe();
    }
}
